import React from "react";
import Button from "@mui/material/Button";
import RightworkSelect from "../../../../../components/shared/RightworkSelect";
import EmployeeProfileSectionCard from "./EmployeeProfileSectionCard";
import { postRightworkAPI } from "../../utilities/requests";
import { API_post_store_team_member_non_labor_account } from "../../../../../constants";

const VALUE_FOR = { labor: false, non_labor: true };

export default function EmployeeLaborAccountSection({ selectedTeamMemberData: member, homeStore, fetchStoreTeamMember }) {
  const id = React.useId();
  const stored = member?.is_non_labor_account ? "non_labor" : "labor";
  // The saved value: the prop, or what this form last saved (the member
  // refresh that updates the prop runs in the background).
  const [baseline, setBaseline] = React.useState(stored);
  const [value, setValue] = React.useState(stored);
  const [saving, setSaving] = React.useState(false);
  const [status, setStatus] = React.useState(null); // "saved" | error message

  // A refreshed saved value replaces the field only while it has no edit
  // (or for a different employee), so a refresh landing mid-edit keeps it.
  const lastBaseline = React.useRef(stored);
  const lastMember = React.useRef(member?.id);
  React.useEffect(() => {
    const memberChanged = lastMember.current !== member?.id;
    setValue((current) => (memberChanged || current === lastBaseline.current ? stored : current));
    setBaseline(stored);
    lastBaseline.current = stored;
    lastMember.current = member?.id;
  }, [member?.id, stored]);

  const dirty = value !== baseline;

  const submit = async (event) => {
    event.preventDefault();
    if (!dirty || saving) return;
    setSaving(true);
    setStatus(null);
    try {
      await postRightworkAPI(API_post_store_team_member_non_labor_account, {
        store_id: homeStore,
        store_team_member_id: member.id,
        is_non_labor_account: VALUE_FOR[value],
      });
      setBaseline(value);
      lastBaseline.current = value;
      setStatus("saved");
      fetchStoreTeamMember?.(false, member.id);
    } catch (error) {
      setStatus("Account classification could not be saved. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <EmployeeProfileSectionCard header="Labor reporting" bodyClassName="p-0">
      <form onSubmit={submit} noValidate>
        <div className="px-6 py-5">
          <label id={`${id}-label`} htmlFor={id} className="mb-2 block text-sm font-medium text-slate-900">
            Account classification
          </label>
          <div className="max-w-sm">
            <RightworkSelect
              id={id}
              value={value}
              onChange={(event) => { setStatus(null); setValue(event.target.value); }}
              disabled={saving}
              describedBy={`${id}-help`}
              options={[
                { value: "labor", label: "Employee account" },
                { value: "non_labor", label: "Device / non-labor account" },
              ]}
            />
          </div>
          <p id={`${id}-help`} className="mt-2 text-sm leading-5 text-slate-500">
            Non-labor punches remain visible but do not count toward coverage hours or costs. Historical totals refresh in the background.
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-3 border-t border-slate-200 px-6 py-4">
          {status === "saved" && <p role="status" className="mr-auto text-sm text-emerald-700">Account classification saved.</p>}
          {status && status !== "saved" && <p role="alert" className="mr-auto text-sm text-red-700">{status}</p>}
          <Button type="submit" variant="contained" disableElevation disabled={!dirty || saving}
            sx={{ fontFamily: "inherit", textTransform: "none", borderRadius: "8px", paddingX: "20px", paddingY: "8px" }}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </form>
    </EmployeeProfileSectionCard>
  );
}
